import { useCallback, useEffect, useState } from 'react';

import { differenceInMinutes } from 'date-fns';
import { usePathname, useRouter } from 'expo-router';
import { CustomEventNames, EventTypes, logEvent } from 'features/analytics';

import {
  DeliveryStatus,
  RbiOrderStatus,
  ServiceMode,
  useGetUserOrdersQuery,
} from 'generated/graphql-gateway';
import { useAuthContext } from 'state/auth';
import { t } from 'state/intl/translations';
import { TranslationKey } from 'state/intl/types';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useOrderContext } from 'state/order';
import { OrderStatus } from 'state/order/types';
import { getDeliveryHeading } from 'utils/get-delivery-heading';
import { routes } from 'utils/routing';
import { getServiceModeIcon } from 'utils/service-mode';

import { IOrderInProgressRecentOrder, IOrderStatusBarData } from './types';

const DELIVERY_COMPLETED = [
  DeliveryStatus.ORDER_DROPPED_OFF,
  DeliveryStatus.ORDER_ABANDONED,
  DeliveryStatus.ORDER_CANCELLED,
];

const ORDER_PROGRESS_POLLING_DURATION_IN_MS = 10000;

const isPollingForDelivery = ({
  hasDelivery,
  isDeliveryOrderCompleted,
  deliveryStatus,
}: {
  hasDelivery: boolean;
  isDeliveryOrderCompleted: boolean;
  deliveryStatus: DeliveryStatus | undefined;
}) =>
  hasDelivery &&
  !isDeliveryOrderCompleted &&
  (!deliveryStatus || !DELIVERY_COMPLETED.includes(deliveryStatus));

const getStatusBarData = (serviceMode: ServiceMode | undefined): IOrderStatusBarData | null => {
  if (!serviceMode) {
    return null;
  }

  const baseData = {
    icon: getServiceModeIcon(serviceMode),
    cta: { text: t('seeDetails') },
  };

  const headingIdMapping: Record<string, TranslationKey> = {
    [ServiceMode.DRIVE_THRU]: 'driveThruOrderPlaced',
    [ServiceMode.MOBILE_ORDER_DRIVE_THRU]: 'mobileOrderDriveThruOrderPlaced',
    [ServiceMode.TAKEOUT]: 'pickUpOrderPlaced',
    [ServiceMode.EAT_IN]: 'dineInOrderPlaced',
    [ServiceMode.DELIVERY]: 'deliveryOrderPlaced',
  };

  const headingId = headingIdMapping[serviceMode];

  return headingId
    ? {
        ...baseData,
        heading: t(headingId),
      }
    : null;
};

export const useTopOrderStatusBar = (): IOrderStatusBarData => {
  const isMobileOrderForDriveThruLaneEnabled = useFlag(
    LaunchDarklyFlag.ENABLE_MOBILE_ORDER_DRIVE_THRU_LANE
  );
  const isTopOrderStatusBarEnabled = useFlag(LaunchDarklyFlag.ENABLE_TOP_ORDER_STATUS_BAR);
  const notificationExpiredInMinutes = useFlag(LaunchDarklyFlag.TOP_ORDER_STATUS_BAR_TIME_OUT);

  const { navigate } = useRouter();
  const pathname = usePathname();
  const { isAuthenticated, loading } = useAuthContext();
  const { serverOrder } = useOrderContext();
  const [shouldDisplayNotification, setShouldDisplayNotification] = useState<boolean>(true);
  const [currentOrderId, setCurrentOrderId] = useState<string>('');
  const [currentDeliveryStatus, setCurrentDeliveryStatus] = useState<DeliveryStatus>();
  const [queryWasExecuted, setQueryWasExecuted] = useState<boolean>(false);

  const navigateToOrderConfirmation = useCallback(
    (orderId: string) => {
      logEvent(CustomEventNames.ORDER_STATUS_CARD_ORDER_DETAIL_CLICK, EventTypes.Navigation, {
        'Order Id': orderId,
      });
      navigate(`${routes.orderConfirmation}/${orderId}`);
    },
    [navigate]
  );

  // start polling for recent user orders
  const { data, startPolling, stopPolling } = useGetUserOrdersQuery({
    variables: {
      limit: 1,
      orderStatuses: [
        RbiOrderStatus.INSERT_SUCCESSFUL,
        RbiOrderStatus.UPDATE_SUCCESSFUL,
        RbiOrderStatus.REFUND_SUCCESSFUL,
      ],
    },
    skip: !isAuthenticated,
  });

  useEffect(() => {
    if ((!loading && !isAuthenticated) || (!queryWasExecuted && data)) {
      setQueryWasExecuted(true);
    }
  }, [isAuthenticated, loading, data, queryWasExecuted]);

  // Upon order confirmation, data from `useGetUseOrdersQuery` is not updated with newly placed order
  // Force a refetch to update query with new confirm order instead of previous order
  const isServerOrderSuccessful: boolean = serverOrder?.status === OrderStatus.INSERT_SUCCESSFUL;

  const order: IOrderInProgressRecentOrder = data?.userOrders?.orders?.[0];

  const orderServiceMode = order?.cart.serviceMode;
  const statusBarData = getStatusBarData(orderServiceMode);

  const isDeliveryOrderCompleted =
    !!order?.delivery &&
    order.delivery.status != null &&
    DELIVERY_COMPLETED.includes(order.delivery.status);

  const notificationIsExpired =
    order?.updatedAt &&
    Math.abs(differenceInMinutes(new Date(), new Date(order.updatedAt))) >
      notificationExpiredInMinutes;

  // Update notification for new orders being placed
  useEffect(() => {
    const order = data?.userOrders?.orders?.[0];
    if (order && order.rbiOrderId !== currentOrderId) {
      setShouldDisplayNotification(true);
      setCurrentOrderId(order.rbiOrderId);
    }
  }, [currentOrderId, data]);

  // Update notification for delivery status changes
  useEffect(() => {
    const order = data?.userOrders?.orders?.[0];
    if (order?.delivery?.status && order?.delivery.status !== currentDeliveryStatus) {
      setShouldDisplayNotification(true);
      setCurrentDeliveryStatus(order?.delivery?.status);
    }
  }, [currentDeliveryStatus, data]);

  // Reset notification after successful server order
  useEffect(() => {
    // reactivate shouldDisplayNotification after closing first successful server order notification
    if (isServerOrderSuccessful) {
      setShouldDisplayNotification(true);
    }
  }, [isServerOrderSuccessful]);

  useEffect(() => {
    //  Hiding it for challenge details
    if (pathname.includes(routes.loyaltyChallengeDetails)) {
      setShouldDisplayNotification(false);
    }
  }, [pathname]);

  if (
    !isTopOrderStatusBarEnabled ||
    !isAuthenticated ||
    !shouldDisplayNotification ||
    !statusBarData ||
    notificationIsExpired ||
    order?.status === RbiOrderStatus.REFUND_SUCCESSFUL
  ) {
    return {
      queryWasExecuted,
      icon: '',
      heading: '',
      cta: {
        text: '',
      },
    };
  }

  if (
    isPollingForDelivery({
      hasDelivery: !!order?.delivery,
      isDeliveryOrderCompleted,
      deliveryStatus: order?.delivery?.status ?? undefined,
    })
  ) {
    startPolling(ORDER_PROGRESS_POLLING_DURATION_IN_MS);
  }

  const orderId = currentOrderId;
  const isDelivery = orderServiceMode === ServiceMode.DELIVERY;

  // PICKUP
  if (order && statusBarData && !isDelivery) {
    // DRIVE_THRU when Mobile Pickup is Enabled
    if (
      orderServiceMode === ServiceMode.MOBILE_ORDER_DRIVE_THRU ||
      (orderServiceMode === ServiceMode.DRIVE_THRU && isMobileOrderForDriveThruLaneEnabled)
    ) {
      statusBarData.heading = t('driveThruMobileOrderPlaced');
    }
    return {
      ...statusBarData,
      cta: {
        ...statusBarData.cta,
        action: () => navigateToOrderConfirmation(orderId),
      },
      queryWasExecuted,
    };
  }

  // DELIVERY
  if (isDelivery && statusBarData) {
    if (isDeliveryOrderCompleted) {
      stopPolling();
      return {
        queryWasExecuted,
        icon: '',
        heading: '',
        cta: { text: '' },
      };
    }

    const deliveryStatus = order?.delivery?.status;
    const headingText = deliveryStatus
      ? t(getDeliveryHeading({ deliveryStatus }))
      : statusBarData.heading;

    return {
      ...statusBarData,
      heading: headingText,
      cta: {
        ...statusBarData.cta,
        action: () => navigateToOrderConfirmation(orderId),
      },
      queryWasExecuted,
    };
  }

  // Stop polling for recent user orders
  stopPolling();
  return {
    queryWasExecuted,
    icon: '',
    heading: '',
    cta: {
      text: '',
    },
  };
};
